import React, {
  Component,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  AulaEditor,
  getActiveEntities,
  hasError,
  hasSessionScanPendingV2,
  isPersistable,
} from '@ublend-npm/aula-editor';
import { API_URL, FILE_URL } from '@core/constants/endpoints';
import { isAntivirusFileScanEnabled } from '@core/constants/launchDarklyFeatureFlags';
import * as actionTypes from '@core/constants/actions';
import { getFileScanStatus } from '@core/selectors/fileScan';
import { isEmptyElement } from '@ublend-npm/aula-editor-utils';
import { analyticsTrackEvent } from '@core/utils/analytics';
import Raven from '@core/utils/raven';

type EditorStatus = {
  hasError: boolean;
  isPersistable: boolean;
  isUpdating: boolean;
};

type Props = {
  editorState: any;
  onChange: (value: any) => void;
  onFilesStatusChange: (status: EditorStatus) => void;
  spaceId: string;
};

export type ExtensionDescriptionEditorRef = {
  focus: () => void;
};

type EditorErrorBoundaryProps = {
  children: React.ReactNode;
};

type EditorErrorBoundaryState = {
  hasError: boolean;
};

class EditorErrorBoundary extends Component<
  EditorErrorBoundaryProps,
  EditorErrorBoundaryState
> {
  state = { hasError: false };

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    Raven.captureException(error, {
      extra: { componentStack: info.componentStack },
      tags: { context: 'errorBoundary' },
    });
    this.setState({ hasError: true });
  }

  render() {
    const { hasError: editorHasError } = this.state;
    const { children } = this.props;
    if (editorHasError) return null;
    return children;
  }
}

const isAnyFileUploading = (entities: { [key: string]: any } = {}) =>
  Object.values(entities).some(
    (entity) =>
      entity.type === 'file' &&
      !entity.failed &&
      (!entity.data?.fileUrl || entity.transient?.progress != null)
  );

const ExtensionDescriptionEditor = forwardRef<
  ExtensionDescriptionEditorRef,
  Props
>(({ editorState, onChange, onFilesStatusChange, spaceId }, ref) => {
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const trackedStart = useRef(false);
  const flags = useFlags();
  const fileScanStatus = useSelector(getFileScanStatus);
  const fileScanBySession = useSelector(
    (state: any) => state.data.fileScanBySession || {}
  );
  const enableFileScanStatus = isAntivirusFileScanEnabled(flags);
  const config = useMemo(
    () => ({
      apiUrl: API_URL(),
      filesUrl: FILE_URL(),
      spaceId,
      ...(enableFileScanStatus && {
        enableFileScanStatus: true,
        fileScanBySession,
        fileScanStatus,
      }),
    }),
    [enableFileScanStatus, fileScanBySession, fileScanStatus, spaceId]
  );

  const status = useMemo(() => {
    const entities = editorState?.context?.entities || {};
    const hasEntities = Object.keys(entities).length > 0;
    const uploading = hasEntities
      ? isAnyFileUploading(getActiveEntities(editorState))
      : false;

    return {
      hasError: hasEntities ? hasError(editorState, config) : false,
      isPersistable: hasEntities ? isPersistable(editorState, config) : true,
      isUpdating:
        uploading ||
        (enableFileScanStatus ? hasSessionScanPendingV2(config) : false),
    };
  }, [config, editorState, enableFileScanStatus]);

  useEffect(() => {
    onFilesStatusChange(status);
  }, [onFilesStatusChange, status]);

  useImperativeHandle(ref, () => ({
    focus: () => {
      const editor = editorContainerRef.current?.querySelector<HTMLElement>(
        '[contenteditable="true"]'
      );
      if (editor) editor.focus();
    },
  }));

  const handleChange = (nextEditorState: any) => {
    const isEditorEmpty = nextEditorState.value.every((node: any) =>
      isEmptyElement(node)
    );
    if (trackedStart.current && isEditorEmpty) trackedStart.current = false;
    if (!trackedStart.current && !isEditorEmpty) {
      trackedStart.current = true;
      analyticsTrackEvent('assignmentExtensions', {
        activeClassroom: spaceId,
        itemType: undefined,
      });
    }
    onChange(nextEditorState);
  };

  return (
    <div ref={editorContainerRef}>
      <EditorErrorBoundary>
        <AulaEditor
          autoFocus={false}
          config={config}
          contentType="assignmentExtensions"
          cursorPosition="start"
          editorState={editorState}
          onChange={handleChange}
          resetEntityScanStatus={(id: string, s3Key: string) =>
            dispatch({
              type: actionTypes.RESET_FILE_SCAN_STATUS,
              payload: { id, s3Key },
            })
          }
          responsiveFlag
        />
      </EditorErrorBoundary>
    </div>
  );
});

ExtensionDescriptionEditor.displayName = 'ExtensionDescriptionEditor';

export default ExtensionDescriptionEditor;
